/**
 * O que faz: Centraliza envio da análise SEO de Perfil, histórico, progresso simulado e modo background.
 * Onde usa: Tela /seo-perfil e passo de onboarding.
 * Conexões: Supabase Auth, Edge Function analyze-seo-profile e tabela seo_profile_analyses.
 * Tabelas: seo_profile_analyses (SELECT de histórico pela clínica ativa).
 * Data: 14/05/2026
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useClinica } from '@/contexts/ClinicaContext';
import type { SeoProfileAnalysisResult, SeoProfileRequest } from '@/types/seoProfile';
import { toast } from 'sonner';

const ANALYSIS_STEPS = [
  'Conectando ao Google Maps...',
  'Coletando dados do perfil...',
  'Processando avaliações e fotos...',
  'Analisando presença digital com inteligência artificial...',
  'Finalizando diagnóstico...',
];

const STEP_DELAYS_MS = [0, 5000, 14000, 28000, 50000];

export function useSeoProfileAnalysis() {
  const { clinicaId } = useClinica();
  const [currentAnalysis, setCurrentAnalysis] = useState<SeoProfileAnalysisResult | null>(null);
  const [history, setHistory] = useState<SeoProfileAnalysisResult[]>([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [analyzingStep, setAnalyzingStep] = useState<string>(ANALYSIS_STEPS[0]);
  const [isBackground, setIsBackground] = useState(false);

  const stepTimersRef = useRef<ReturnType<typeof setTimeout>[]>([]);

  const clearStepTimers = () => {
    stepTimersRef.current.forEach(clearTimeout);
    stepTimersRef.current = [];
  };

  const startStepProgression = () => {
    clearStepTimers();
    setAnalyzingStep(ANALYSIS_STEPS[0]);

    ANALYSIS_STEPS.forEach((step, index) => {
      if (index === 0) return;
      const timer = setTimeout(() => {
        setAnalyzingStep(step);
      }, STEP_DELAYS_MS[index]);
      stepTimersRef.current.push(timer);
    });
  };

  const fetchHistory = useCallback(async () => {
    if (!clinicaId) return;

    setLoadingHistory(true);

    try {
      const { data, error } = await supabase
        .from('seo_profile_analyses')
        .select('id, input_mode, analysis_source, status, overall_score, channel_scores, extracted_signals, recommendations, summary, debug_events, error_message, created_at')
        .eq('clinica_id', clinicaId)
        .order('created_at', { ascending: false })
        .limit(10);

      if (error) throw error;

      setHistory((data ?? []) as SeoProfileAnalysisResult[]);
    } catch (error) {
      console.error('[SEO Profile] Erro ao buscar histórico:', error);
    }
  }, [clinicaId]);

  const analyzeProfile = useCallback(async (request: Omit<SeoProfileRequest, 'clinica_id'>) => {
    if (!clinicaId) {
      toast.error('Clínica ativa não encontrada.');
      return null;
    }

    setAnalyzing(true);
    setIsBackground(false);
    startStepProgression();

    try {
      const payload: SeoProfileRequest = { ...request, clinica_id: clinicaId };

      const { data, error } = await supabase.functions.invoke('analyze-seo-profile', {
        body: payload,
      });

      if (error) throw error;
      if (data?.error) throw new Error(data.error);

      const analysis = data.analysis as SeoProfileAnalysisResult;
      setCurrentAnalysis(analysis);
      setHistory((items) => [analysis, ...items.filter((item) => item.id !== analysis.id)].slice(0, 10));

      toast.success('Diagnóstico concluído com sucesso.');
      return analysis;
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Erro ao analisar o perfil digital.';
      toast.error(message);
      return null;
    } finally {
      clearStepTimers();
      setAnalyzing(false);
      setIsBackground(false);
      setAnalyzingStep(ANALYSIS_STEPS[0]);
    }
  }, [clinicaId]);

  useEffect(() => {
    fetchHistory();
  }, [fetchHistory]);

  useEffect(() => {
    return () => clearStepTimers();
  }, []);

  return {
    currentAnalysis,
    history,
    loadingHistory,
    analyzing,
    analyzingStep,
    isBackground,
    setIsBackground,
    analyzeProfile,
    fetchHistory,
    setCurrentAnalysis,
  };
}
